import { SparklesIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';

import { HAS_FREE_PLAN } from '@/constants/common';
import { Preset } from '@/constants/presets';

const TemplateForm = dynamic(() => import('@/components/PublicTemplateForm'));

interface InteriorHeroSectionProps {
  preset: Preset;
}

const InteriorHeroSection = ({ preset }: InteriorHeroSectionProps) => {
  const { t } = useLocale('tools');

  return (
    <div
      className={clsxm(
        preset.isFree ? 'bg-gray-50' : 'bg-white',
        'relative dark:bg-slate-900',
      )}
    >
      <LandingPageNavigation skipUserContext />

      <section className='relative pb-8 sm:pb-16 md:pb-20 lg:pb-28 xl:pb-48'>
        {/* Video Background */}
        <div className='absolute inset-0 z-0 overflow-hidden'>
          <div className='absolute inset-0 z-10 bg-black/50' /> {/* Overlay */}
          <video
            className='absolute h-full w-full object-cover'
            autoPlay
            loop
            muted
            playsInline
          >
            <source
              src='https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/final_db54d114-bdba-4a0d-8485-cc9a17b4eace.mp4'
              type='video/mp4'
            />
          </video>
        </div>

        <div className='relative isolate z-20'>
          <div className='mx-auto mt-10 flex h-full flex-col items-center justify-center sm:mt-12 sm:px-6 lg:px-8 '>
            <div className='w-full max-w-4xl text-center xl:max-w-5xl'>
              <h1
                className={clsxm(
                  'mt-32 px-2 text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl xl:mt-48',
                )}
              >
                <span className='block xl:inline'>
                  {t(preset.title) || preset.title}
                </span>{' '}
              </h1>
              <p className='mx-auto mt-3 max-w-4xl px-2 text-2xl text-gray-200 sm:mt-5 md:mt-5'>
                {t(preset.description) || preset.description}
              </p>
              <div className='mt-5 w-full sm:mt-8 sm:flex sm:justify-center'>
                {!preset.isFree ? (
                  <div className='mb-5 rounded-md sm:mb-0'>
                    <ButtonLink
                      variant='dark'
                      className='rounded-md px-8 py-3 text-base font-medium md:px-10 md:py-4 md:text-lg'
                      href={
                        HAS_FREE_PLAN
                          ? `/login?preset=${encodeURIComponent(preset.loginPreset || preset.slug)}`
                          : '/#pricing'
                      }
                    >
                      <span className='rainbow flex items-center'>
                        <SparklesIcon className='mr-2 h-7 w-7 text-white' />
                        {preset.onlyForPaidPlans
                          ? t('Get started')
                          : t('Get started for free')}
                      </span>
                    </ButtonLink>
                  </div>
                ) : (
                  <div className='mx-auto w-full max-w-md text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8'>
                    <TemplateForm preset={preset} />
                  </div>
                )}
              </div>
            </div>
            <div className='flex w-full flex-col flex-wrap gap-x-4 gap-y-2 text-center text-gray-200 sm:mt-10 sm:flex-row sm:justify-center lg:text-lg'>
              <div>🚀 {t('Powered by best AI models')}</div>
              <div>🌍 {t('Supports 40+ languages')}</div>
              <div>💳 {t('No credit card required')}</div>
              <div>⭐ {t('5-star ratings')}</div>
            </div>
          </div>
        </div>
      </section>
      <LogosCloud />
    </div>
  );
};

export default InteriorHeroSection;
